// components/landing/TeamPage.tsx
// ---------------------------------------------------------------------------
// Public team page showcasing Athena Tech's co-founders and leadership.
// Polished adaptation of athenatechai.com/management-team/ with consistent
// Athena design language (deep navy, glass cards, periwinkle accents).
// ---------------------------------------------------------------------------

import {
  alpha,
  Avatar,
  Box,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SEOHead from "../common/SEOHead";
import { athenaColors } from "./colors";
import LandingNavBar from "./LandingNavBar";
import LandingFooter from "./sections/LandingFooter";

// ---------------------------------------------------------------------------
// Team data
// ---------------------------------------------------------------------------
interface TeamMember {
  name: string;
  role: string;
  image: string;
  bio: string[];
}

const TEAM: TeamMember[] = [
  {
    name: "Joan Ficapal Vila",
    role: "Co-Founder & CEO",
    image: "/images/team/joan.jpg",
    bio: [
      "Joan Ficapal Vila holds a degree in Telecommunications Engineering from the Polytechnic University of Catalonia (UPC) and a Double MSc in Data Science and Entrepreneurship from Politecnico di Milano and KTH Royal Institute of Technology.",
      "At Hospital Clinic de Barcelona, he founded Athena Tech to address the growing need for structured oncological data management. Under his leadership, the platform has expanded to support clinical decisions and research across more than 30 institutions in multiple countries, backed by a European Commission grant.",
      "His background spans NLP-focused AI development at a Swedish startup and data science at Zurich Insurance, giving him a broad perspective on complex data challenges. His work is driven by a commitment to building technology that meaningfully serves healthcare professionals and their patients.",
    ],
  },
  {
    name: "Dr. Josep Malvehy Guilera",
    role: "Co-Founder & Chief Medical Officer",
    image: "/images/team/malvehy.png",
    bio: [
      "Josep Malvehy has served as director of the Skin Cancer Program at the Hospital Clinic of Barcelona since 2003 and directs the Innovation Program of the Institut de Medicina i Dermatologia (ICMID). He is accredited as a full professor by the Catalan University Quality Agency (AQU) since 2021.",
      "He chairs the Task Force of Artificial Intelligence of the EADV and holds board positions at the World Melanoma Society, EADO, the International Dermoscopy Society, the International Group of Confocal Microscopy, and the European Dermatology Forum.",
      "An investigator at IDIBAPS, he is the author of more than 450 scientific publications and lead author of the European guidelines for melanoma and skin cancers. He has served as principal investigator on competitive research projects totaling more than 12 million euros in grant funding.",
    ],
  },
  {
    name: "Dra. Susana Puig Sarda",
    role: "Co-Founder & Medical Advisor",
    image: "/images/team/susana.jpg",
    bio: [
      "Dr. Susana Puig is a world leader in the diagnosis and treatment of melanoma and skin cancer, with deep expertise in genetics, immunology, dermoscopy, and confocal microscopy.",
      "She is Full Professor of Dermatology at the University of Barcelona, Head of Department at the Hospital Clinic of Barcelona, and leader of the research group \"Melanoma: Image, Genetics and Immunology\" at IDIBAPS.",
      "She is among the most published dermatologists in Spain, with more than 450 international publications in high-impact journals.",
    ],
  },
  {
    name: "Dr. Sebastian Podlipnik",
    role: "Co-Founder & Medical Advisor",
    image: "/images/team/podlipnik.jpg",
    bio: [
      "Sebastian Podlipnik completed his dermatology training at the Hospital Clinic of Barcelona, where he received the CLINIC-MSD award for the best residents of his generation. He is currently a postdoctoral researcher and practicing dermatologist.",
      "His research focuses on epidemiological factors and prognostic indicators in melanoma patients. He published his doctoral thesis, \"Prognosis in melanoma: Optimization of monitoring schemes,\" receiving a Cum Laude qualification from the University of Barcelona. He is co-author of more than 100 high-impact publications.",
    ],
  },
  {
    name: "Dr. Santiago Iriso",
    role: "Co-Founder & Medical Advisor",
    image: "/images/team/iriso.png",
    bio: [
      "Dr. Santiago Iriso brings more than 20 years of experience at the intersection of healthcare and information technology, with a career spanning project management, European research, and eHealth solution design.",
      "A primary care physician for nearly 30 years, he has dedicated his career to integrating IT solutions into the healthcare ecosystem, helping professionals and patients interact effectively with digital tools. He is an active member of multiple national and international healthcare IT and innovation societies.",
    ],
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function TeamPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navH = (theme.custom?.navbarHeight as number) || 64;

  // Navbar callbacks (minimal for this standalone page)
  const handleSignIn = () => {
    window.location.href = "/";
  };
  const handleNavClick = (href: string) => {
    if (href.startsWith("/")) {
      window.location.href = href;
    } else {
      // Hash links: go back to landing and scroll
      window.location.href = `/${href}`;
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: athenaColors.deepNavy,
        color: theme.palette.common.white,
      }}
    >
      <SEOHead
        title="Our Team"
        description="Meet the co-founders of Athena Tech: world-renowned oncologists, data scientists, and healthcare technology experts advancing cancer care through AI-powered clinical data management."
        path="/team"
      />
      <LandingNavBar onSignIn={handleSignIn} onNavClick={handleNavClick} />

      {/* Spacer for fixed navbar */}
      <Box sx={{ height: `${navH}px` }} />

      {/* Hero header */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          px: { xs: 3, md: 6 },
          textAlign: "center",
          background: `linear-gradient(180deg, ${athenaColors.deepNavy} 0%, ${alpha(athenaColors.royalBlue, 0.35)} 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="overline"
            sx={{
              display: "block",
              letterSpacing: 4,
              color: athenaColors.periwinkle,
              mb: 2,
              fontSize: "0.8rem",
            }}
          >
            THE PEOPLE BEHIND ATHENA
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 4,
              fontSize: { xs: "2rem", md: "2.8rem" },
              background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${athenaColors.periwinkle} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Team
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: alpha(theme.palette.common.white, 0.8),
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.1rem" },
              maxWidth: 720,
              mx: "auto",
            }}
          >
            Our strength lies in the convergence of clinical expertise and data
            science. World-renowned oncologists, experienced data scientists, and
            healthcare technology specialists collaborate to advance cancer care
            through rigorous, data-driven innovation. Together, we ensure that
            Athena remains both clinically relevant and technically precise,
            bridging the gap between medical practice and artificial
            intelligence.
          </Typography>
        </Container>
      </Box>

      {/* Team grid */}
      <Container
        maxWidth="lg"
        sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 4 } }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 4, md: 6 },
          }}
        >
          {TEAM.map((member, idx) => {
            const isEven = idx % 2 === 0;

            return (
              <Box
                key={member.name}
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    md: isEven ? "row" : "row-reverse",
                  },
                  gap: { xs: 3, md: 5 },
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  background: alpha(theme.palette.common.white, 0.04),
                  backdropFilter: "blur(8px)",
                  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                  transition:
                    "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 16px 48px ${alpha(athenaColors.royalBlue, 0.2)}`,
                    borderColor: alpha(athenaColors.periwinkle, 0.2),
                  },
                }}
              >
                {/* Photo */}
                <Box
                  sx={{
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Avatar
                    src={member.image}
                    alt={member.name}
                    sx={{
                      width: isMobile ? 140 : 180,
                      height: isMobile ? 140 : 180,
                      border: `3px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
                      boxShadow: `0 8px 32px ${alpha(athenaColors.deepNavy, 0.5)}`,
                    }}
                  />
                </Box>

                {/* Text */}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      fontSize: { xs: "1.2rem", md: "1.4rem" },
                      color: theme.palette.common.white,
                    }}
                  >
                    {member.name}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: athenaColors.periwinkle,
                      fontWeight: 500,
                      mb: 2,
                      fontSize: "0.95rem",
                    }}
                  >
                    {member.role}
                  </Typography>

                  {member.bio.map((paragraph, pIdx) => (
                    <Typography
                      key={pIdx}
                      variant="body2"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.75),
                        lineHeight: 1.75,
                        mb: pIdx < member.bio.length - 1 ? 1.5 : 0,
                        fontSize: { xs: "0.88rem", md: "0.92rem" },
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>

      {/* Footer */}
      <LandingFooter />
    </Box>
  );
}
